<template>
  <!-- Custom Currency Dropdown -->
  <div
    ref="dropdownRef"
    :class="{ 'relative mx-2 mb-6 inline-block text-left': true }"
  >
    <button
      type="button"
      aria-haspopup="true"
      :aria-expanded="dropdownOpen"
      :class="{
        'border-gray-300 text-gray-700 hover:bg-gray-50 inline-flex w-full justify-center rounded-3xl border bg-www-color-white px-2 py-1 text-sm font-medium shadow-sm focus:outline-none': true,
      }"
      @click="toggleDropdown"
    >
      {{ defaultCurrency.toUpperCase() }}
      <svg
        :class="{
          'ml-1 h-5 w-5': true,
        }"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fill-rule="evenodd"
          d="M5.23 7.21a.75.75 0 011.06.02L10 10.584l3.71-3.354a.75.75 0 111.02 1.1l-4.25 3.846a.75.75 0 01-1.02 0l-4.25-3.846a.75.75 0 01.02-1.06z"
          clip-rule="evenodd"
        />
      </svg>
    </button>
    <transition
      enter-active-class="transition ease-out duration-200"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition ease-in duration-150"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-show="dropdownOpen"
        :class="{
          'ring-black absolute left-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-www-color-white shadow-lg ring-1 ring-opacity-5': true,
        }"
      >
        <div class="py-1">
          <button
            v-for="currency in currencyOptions"
            :key="currency.code"
            :class="{
              'text-gray-700 hover:bg-gray-100 block w-full px-4 py-2 text-left text-sm': true,
            }"
            @click="selectCurrency(currency)"
          >
            {{ currency.label }}
          </button>
        </div>
      </div>
    </transition>
  </div>
  <!-- End Custom Currency Dropdown -->
</template>

<script setup lang="ts">
import type { CurrencyDropDown } from '~/sanity/types';

defineProps<CurrencyDropDown>();
const currencyData = await useCurrency();
const { currencyOptions, setCurrency } = currencyData;
const defaultCurrency = useState<string>('default-currency');
const dropdownOpen = ref(false);
const dropdownRef = ref<HTMLElement | null>(null);

const selectCurrency = (currency: { code: string; label: string }) => {
  setCurrency(currency.code);

  // Update/add 'in' param, preserve others
  const url = new URL(window.location.href);
  url.searchParams.set('in', currency.code);
  history.pushState(null, '', url.pathname + url.search + url.hash);

  dropdownOpen.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target as Node)) {
    dropdownOpen.value = false;
  }
};

const toggleDropdown = () => {
  dropdownOpen.value = !dropdownOpen.value;
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Custom dropdown styles (optional, can be moved to a global style file) */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity 0.2s;
}
.dropdown-enter,
.dropdown-leave-to {
  opacity: 0;
}
</style>
